import { useEffect, useMemo, useState } from "react";
import { CircularProgress, useTheme } from "@mui/material";
import { FormPropertyProps } from "../../FormProperty";
import { FoodsRadioList } from "@foodpilot/foods";
import { getPropertyDefaultOption, getPropertyGenericOption, findOptionByCode } from "src/utils/dataListOptions";
import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import { getDataListOptionsWithOverrides } from "src/utils/ssq";
import { usePropertyContext } from "../../context/usePropertyContext";
import { FieldValueLayout } from "../FieldValueLayout";
import { saveOrDeleteOptionAnswer } from "./saveOrDeleteOptionAnswer";

export const Radio = (props: FormPropertyProps) => {
    const theme = useTheme();

    const { property, position, showSubscore, dimensionValue } = props;
    const { post, getElement, onUpdate, onDelete, canEdit, dataListValueOverrides } = usePropertyContext();

    const element = getElement(property.id, position);

    const options = useMemo(
        () => getDataListOptionsWithOverrides(property.dataList, dataListValueOverrides),
        [property.dataList, dataListValueOverrides],
    );

    const propertyDefaultOption = getPropertyDefaultOption(property, options);
    const propertyGenericOption = getPropertyGenericOption(property, options);

    // Initialize with either the element value or default value
    const selectedCode = element?.dataListOptionCodes?.[0];
    const initialValue: DataListOption | undefined = findOptionByCode(options, selectedCode);

    const [value, setValue] = useState<DataListOption | undefined>(initialValue);
    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    if (!post) return <CircularProgress />;

    const updatePropertyValue = (value?: DataListOption, isGenericValue?: boolean) => {
        saveOrDeleteOptionAnswer(
            { onUpdate, onDelete },
            { postId: post.id, propertyId: Number(property.id), position: Number(props.position ?? 0) },
            value,
            isGenericValue,
        );
    };

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                forceHideScore: showSubscore === false,
                isGenericValueSelected: genericValueSelected,
                defaultValue: propertyDefaultOption?.label ?? null,
                genericValue: propertyGenericOption?.label ?? null,
                setDefaultValue: () => {
                    if (propertyDefaultOption === undefined) return;
                    if (value?.id === propertyDefaultOption.id) {
                        return;
                    }

                    setValue(propertyDefaultOption);
                    updatePropertyValue(propertyDefaultOption);
                },
                setGenericValue: () => {
                    if (propertyGenericOption === undefined) return;

                    setGenericValueSelected((prevState) => !prevState);
                    setValue(!genericValueSelected ? propertyGenericOption : undefined);
                    updatePropertyValue(
                        !genericValueSelected ? propertyGenericOption : undefined,
                        !genericValueSelected,
                    );
                },
                dimensionValue,
                hideUnit: true,
            }}
        >
            <FoodsRadioList
                values={options}
                selectedItem={value}
                onChange={(newValue: DataListOption) => {
                    setValue(newValue);
                    updatePropertyValue(newValue);
                }}
                getId={(option: DataListOption) => option.id}
                getName={(option: DataListOption) => option.label}
                FormControlProps={{
                    sx: {
                        flexDirection: "column",
                        paddingLeft: "4px",
                        // border: `1px solid blue`,
                    },
                }}
                FormControlLabelProps={{
                    sx: {
                        gap: "3px",
                        // marginRight: "28px",
                        // alignItems: "center",
                        ".MuiTypography-root": {
                            ...theme.typography.body,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                        },
                    },
                }}
                RadioProps={{
                    disabled: !canEdit || property.readonly || genericValueSelected,
                }}
            />
        </FieldValueLayout>
    );
};
